import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { US_STATES } from "@/lib/api";
import { useCoverage } from "@/hooks/usePublicConfig";
import { Globe, BarChart3 } from "lucide-react";

export default function Jurisdictions() {
  const { intl, countries: COUNTRIES } = useCoverage();
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get("/entries", { params: { limit: 500 } })
      .then((r) => setEntries(r.data))
      .catch((err) => console.error("Failed to load entries", err));
  }, []);

  const counts = useMemo(() => {
    const m = {};
    entries.forEach((e) => { m[e.jurisdiction] = (m[e.jurisdiction] || 0) + 1; });
    return m;
  }, [entries]);

  const restrictsCount = useMemo(() => {
    const m = {};
    entries.forEach((e) => {
      if (e.impact === "restricts") m[e.jurisdiction] = (m[e.jurisdiction] || 0) + 1;
    });
    return m;
  }, [entries]);

  const internationalEntries = useMemo(
    () => entries.filter((e) => e.jurisdiction_level === "international"),
    [entries]
  );
  // Countries with at least one entry, plus the curated list (so empty cards still show).
  const internationalCountries = useMemo(() => {
    const withEntries = new Set(internationalEntries.map((e) => e.jurisdiction).filter(Boolean));
    return [...new Set([...withEntries, ...COUNTRIES])];
  }, [internationalEntries, COUNTRIES]);

  const stateList = US_STATES.slice(1); // exclude Federal here

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="border-b border-black/15 pb-6 mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">/by jurisdiction</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">Jurisdictions</h1>
          <p className="mt-3 text-base text-[#333] max-w-2xl">
            {intl
              ? "Browse documented actions by federal authority, U.S. state, or country. International coverage grows as entries are added."
              : "Browse documented actions by federal authority or U.S. state."}
          </p>
        </div>
        <Link
          to="/compare"
          data-testid="compare-link"
          className="inline-flex items-center gap-2 border-2 border-black bg-white px-5 h-12 font-mono-plex text-sm uppercase tracking-wider hover:bg-black hover:text-white transition"
        >
          <BarChart3 className="w-4 h-4" /> Compare side-by-side
        </Link>
      </div>

      {/* Federal block */}
      <div className="mb-10">
        <Link
          to="/tracker?jurisdiction=Federal"
          data-testid="jurisdiction-federal"
          className="block bg-black text-white p-8 border border-black hover:bg-[#222] transition"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono-plex text-[11px] tracking-[0.25em] uppercase opacity-60">/federal</div>
              <h2 className="font-display text-3xl font-bold mt-2">United States · Federal</h2>
              <p className="mt-2 opacity-80 max-w-xl">Congress · the Supreme Court · executive agencies · the White House.</p>
            </div>
            <div className="font-display text-5xl font-black">{counts["Federal"] || 0}</div>
          </div>
        </Link>
      </div>

      {/* State grid */}
      <div className="overline mb-3">/u.s. states</div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-black/15 border border-black/15 mb-12">
        {stateList.map((s) => {
          const total = counts[s] || 0;
          const r = restrictsCount[s] || 0;
          const has = total > 0;
          return (
            <Link
              key={s}
              to={`/jurisdictions/${encodeURIComponent(s)}`}
              data-testid={`jurisdiction-${s}`}
              className={`p-4 ${has ? "bg-white hover:bg-black hover:text-white" : "bg-[#F2F2F2] text-[#555]"} transition-colors duration-150`}
            >
              <div className="font-display text-base font-bold leading-tight">{s}</div>
              <div className="mt-2 font-mono-plex text-[11px] uppercase tracking-wider flex justify-between">
                <span>{total} entries</span>
                {r > 0 && <span className="text-[#D32F2F]">{r} restrict</span>}
              </div>
            </Link>
          );
        })}
      </div>

      {/* International section (hidden while international coverage is paused) */}
      {intl && (
      <div className="border-t border-black/15 pt-10" data-testid="jurisdictions-international">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="w-5 h-5" />
          <div className="overline">/international</div>
        </div>
        <h2 className="font-display text-3xl font-bold tracking-tight">Beyond the United States</h2>
        <p className="text-[#333] mt-2 max-w-2xl">
          {internationalEntries.length > 0
            ? `${internationalEntries.length} documented international ${internationalEntries.length === 1 ? "action" : "actions"} on file. Browse by country below.`
            : "International coverage is being built — admins can now add entries from any country. The grid below is seeded with a curated country list; coverage will populate as entries are published."}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-black/15 border border-black/15 mt-6">
          {internationalCountries.map((c) => {
            const total = counts[c] || 0;
            const r = restrictsCount[c] || 0;
            const has = total > 0;
            return (
              <Link
                key={c}
                to={`/jurisdictions/${encodeURIComponent(c)}`}
                data-testid={`jurisdiction-intl-${c}`}
                className={`p-4 ${has ? "bg-white hover:bg-black hover:text-white" : "bg-[#F2F2F2] text-[#666]"} transition-colors duration-150`}
              >
                <div className="font-display text-base font-bold leading-tight">{c}</div>
                <div className="mt-2 font-mono-plex text-[11px] uppercase tracking-wider flex justify-between">
                  <span>{total} entries</span>
                  {r > 0 && <span className="text-[#D32F2F]">{r} restrict</span>}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
